import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { TestResponse } from '@/Domain/api/Action/dto/Action.dto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { HttpTestResponseForm } from '@/Domain/components/Action/HttpRequestActionSheet/TestResponse/TestResponse';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import {
  ValidationCheckError,
  getValidationCheckMessage,
} from '@/Domain/helper/actionValidation.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

export enum TestOutcome {
  Success = 'success',
  UpstreamError = 'upstream_error',
  CallFailed = 'call_failed',
  InvalidConfig = 'invalid_config',
  Rejected = 'rejected',
  RequestFailed = 'request_failed',
  Skipped = 'skipped',
}

export const useTestRequest = () => {
  const { t } = useTranslation('actions_page');
  const [jsonResponse, setJsonResponse] = useState<TestResponse | null>(null);
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.httpRequest.test],
    mutationFn: (data: HttpTestResponseForm) => ActionsApi.testRequest(data),
    onError: (error: AxiosError<ErrorResponse>) => {
      if (error instanceof AxiosError) {
        const firstError = error.response?.data?.errors?.root?.[0];
        const validationChecks = firstError?.options?.validation_checks;

        // Rejected tests used to emit nothing at all, so the failures worth counting were
        // exactly the ones missing from the funnel.
        capture(PostHogEventEnum.ActionTested, {
          status_code: error.status ?? null,
          success: false,
          outcome: validationChecks
            ? TestOutcome.InvalidConfig
            : error.status === HttpStatusCode.BadRequest
              ? TestOutcome.Rejected
              : TestOutcome.RequestFailed,
        });

        if (error.status === HttpStatusCode.BadRequest) {
          if (validationChecks) {
            const message = getValidationCheckMessage(validationChecks as ValidationCheckError, t);
            if (message) {
              toast.error(message);
              return;
            }
          }

          const serverMessage = firstError?.message;
          if (serverMessage) {
            toast.error(serverMessage);
            return;
          }
        }

        toast.error(t('actions_page:sheet.form.validation.test_failed'));
      }
    },
    onSuccess: (data) => {
      // The backend rewrites an in-band failure to exactly { error: string } on a kept
      // upstream 2xx, and a denied run-condition gate to exactly { info: string } with a
      // null code; requiring that single-key shape keeps a legitimate upstream payload
      // that merely contains an "error" field from being misread.
      const soleKey =
        data.response &&
        typeof data.response === 'object' &&
        Object.keys(data.response).length === 1
          ? Object.keys(data.response)[0]
          : null;
      const inBandError = soleKey === 'error' && typeof data.response.error === 'string';
      const skipped =
        data.http_code === null && soleKey === 'info' && typeof data.response.info === 'string';
      const succeeded =
        data.http_code !== null && data.http_code >= 200 && data.http_code < 300 && !inBandError;
      capture(PostHogEventEnum.ActionTested, {
        status_code: data.http_code,
        success: succeeded,
        outcome: succeeded
          ? TestOutcome.Success
          : skipped
            ? TestOutcome.Skipped
            : data.http_code !== null
              ? TestOutcome.UpstreamError
              : TestOutcome.CallFailed,
      });
      setJsonResponse(data);
    },
  });

  const handleTestRequest = (data: HttpTestResponseForm) => mutation.mutate(data);

  return { handleTestRequest, ...mutation, jsonResponse };
};
